import { FormattingRule } from '@ardoq/data-model';
import {
  APIEntityType,
  ColorRangeFormatting,
  HexColor,
  isColorRangeFormatting,
  isListFieldFormatting,
} from '@ardoq/api-types';
import { getFieldLabelOrName } from './utils';
import type { Formatting } from './types';
import { normalizeColorToHex } from '@ardoq/color-helpers';
import { type I18nLocale, t } from '@ardoq/i18n';

const COLOR_RANGE_POINTS: ('min' | 'mid' | 'max')[] = ['min', 'mid', 'max'];

type RuleId = string;
type WarningColorKey = string;
type Message = string;

const getRelevantRulesForDuplicateCheck = (
  currentRule: FormattingRule,
  rulesUsingSameColor: FormattingRule[]
) => {
  if (currentRule.type === APIEntityType.COMPONENT) {
    return rulesUsingSameColor.filter(
      r => r.type === APIEntityType.COMPONENT || r.type === APIEntityType.TAG
    );
  }

  if (currentRule.type === APIEntityType.REFERENCE) {
    return rulesUsingSameColor.filter(
      r => r.type === APIEntityType.REFERENCE || r.type === APIEntityType.TAG
    );
  }

  return rulesUsingSameColor;
};

const normalizeColorToHexLowercase = (color: string): string =>
  normalizeColorToHex(color).toLowerCase();

/** Records the usage of a color by a formatting rule. */
const addColorUsage = (
  rulesByColor: Record<HexColor, FormattingRule[] | undefined>,
  color: HexColor,
  rule: FormattingRule
): void => {
  const lowercaseColor = color.toLowerCase();

  const isRuleAlreadyAssignedToColor =
    rulesByColor[lowercaseColor]?.some(r => r.__id === rule.__id) ?? false;

  if (isRuleAlreadyAssignedToColor) {
    return;
  }

  if (!rulesByColor[lowercaseColor]) {
    rulesByColor[lowercaseColor] = [];
  }

  rulesByColor[lowercaseColor].push(rule);
};

/** Retrieves the label of the value item that matches the specified color in the formatting rule. */
const getMatchingColorValueLabel = (
  i18nLocale: I18nLocale,
  formattingByRuleAndField: Record<string, Partial<Record<string, Formatting>>>,
  rule: FormattingRule,
  color: HexColor
): string | null => {
  const lowercaseColor = normalizeColorToHexLowercase(color);
  const isColorRange = rule.value && isColorRangeFormatting(rule.value);
  const formattingByRule = formattingByRuleAndField[rule.__id] || {};

  if (rule.value && (isListFieldFormatting(rule.value) || isColorRange)) {
    const listValue = rule.value as Record<string, any>;

    let itemWithUsedColor: [string, any] | undefined;

    if (isColorRange) {
      // Check each point in the color range for a matching color
      itemWithUsedColor = COLOR_RANGE_POINTS.map(
        point => [point, listValue[point]] as [string, any]
      ).find(
        ([, config]) =>
          normalizeColorToHexLowercase(config?.color) ===
          normalizeColorToHexLowercase(lowercaseColor)
      );
    } else {
      // Check each item in the list formatting for a matching color
      itemWithUsedColor = Object.entries(listValue).find(
        ([, config]) =>
          config &&
          typeof config === 'object' &&
          config.color &&
          normalizeColorToHexLowercase(config.color) === lowercaseColor
      );
    }

    const itemName = itemWithUsedColor?.[0];

    if (!itemName) {
      return null;
    }

    return isColorRange
      ? t(
          i18nLocale,
          'perspectives:colorWarnings.namedPoint',
          {
            name: itemName,
          },
          '{{name}} point'
        )
      : itemName;
  }

  return (
    (rule.type === APIEntityType.TAG
      ? rule.value
      : getFieldLabelOrName(formattingByRule, rule.field || '')) || null
  );
};

/** Builds a unique key for a color warning based on color and optional value name. */
export const buildColorWarningKey = (
  color: string,
  valueName?: string
): WarningColorKey =>
  `${normalizeColorToHexLowercase(color)}::${valueName || 'global'}`;

/** Builds a warning message if a color is used by multiple formatting rules. */
const buildColorWarning = (
  i18nLocale: I18nLocale,
  formattingByRuleAndField: Record<string, Partial<Record<string, Formatting>>>,
  rulesByColor: Record<HexColor, FormattingRule[] | undefined>,
  color: HexColor,
  currentRule: FormattingRule
) => {
  const lowercaseColor = normalizeColorToHexLowercase(color);
  const rulesUsingColor = rulesByColor[lowercaseColor];

  // No other rules are using this color
  if (!rulesUsingColor || rulesUsingColor.length === 0) {
    return null;
  }

  // Check if the same rule is using this color (usually color-range or list-item rules)
  const sameRule = rulesUsingColor.find(r => r.__id === currentRule.__id);

  // If the same rule is using this color, provide specific warning
  if (sameRule && sameRule.type !== APIEntityType.TAG) {
    const valueItemLabel = getMatchingColorValueLabel(
      i18nLocale,
      formattingByRuleAndField,
      sameRule,
      color
    );
    return valueItemLabel
      ? t(
          i18nLocale,
          'perspectives:colorWarnings.assignedTo',
          { label: valueItemLabel },
          'This color has been assigned to "{{label}}"'
        )
      : null;
  }

  const relevantRules = getRelevantRulesForDuplicateCheck(
    currentRule,
    rulesUsingColor
  );

  if (relevantRules.length === 0) return null;

  const conflictingRule = relevantRules[0];

  const formattingByRule = formattingByRuleAndField[conflictingRule.__id] || {};
  const ruleTitle =
    'field' in conflictingRule
      ? getFieldLabelOrName(formattingByRule, conflictingRule.field || '')
      : conflictingRule.value ||
        (conflictingRule.type === APIEntityType.TAG
          ? t(i18nLocale, 'perspectives:formattingRuleRow.tagLabel', 'Tag')
          : '');

  const maybeItemValue =
    'field' in conflictingRule
      ? getMatchingColorValueLabel(
          i18nLocale,
          formattingByRuleAndField,
          conflictingRule,
          color
        )
      : null;
  const itemValuePart = maybeItemValue ? ` - "${maybeItemValue}"` : '';
  const conflictingRuleInfo =
    ruleTitle === maybeItemValue
      ? `"${ruleTitle}"`
      : `"${ruleTitle}"${itemValuePart}`;

  return t(
    i18nLocale,
    'perspectives:colorWarnings.alsoUsedFor',
    { ruleInfo: conflictingRuleInfo },
    'This color is also used for {{ruleInfo}}. Components matching any of the rules will show this color.'
  );
};

export type RuleColorWarningsMap = Record<WarningColorKey, Message>;

/**
 * Builds a map of color warnings for formatting rules based on color conflicts.
 * @param rules - The list of formatting rules to analyze.
 * @param formattingByRuleAndField - A record of formatting configurations by rule and field.
 */
export const buildColorWarningsMap = (
  i18nLocale: I18nLocale,
  rules: FormattingRule[],
  formattingByRuleAndField: Record<string, Partial<Record<string, Formatting>>>
) => {
  const rulesByColor: Record<HexColor, FormattingRule[] | undefined> = {};
  const colorWarningsByKey: Record<RuleId, RuleColorWarningsMap> = {};

  // Adds a color warning for a specific color and rule if a conflict is detected.
  const maybeAddColorWarning = (
    color: string,
    rule: FormattingRule,
    valueName?: string
  ): void => {
    const colorWarning = buildColorWarning(
      i18nLocale,
      formattingByRuleAndField,
      rulesByColor,
      color,
      rule
    );

    if (colorWarning) {
      const ruleWarningColorKey = buildColorWarningKey(color, valueName);

      if (!colorWarningsByKey[rule.__id]) {
        colorWarningsByKey[rule.__id] = {};
      }

      colorWarningsByKey[rule.__id][ruleWarningColorKey] = colorWarning;
    }
  };

  // Process rules in reverse order to detect color conflicts (display warnings at newest rule).
  const reversedRules = [...rules].reverse();

  reversedRules.forEach(rule => {
    if (!rule.color) {
      return;
    }

    const ruleColor = normalizeColorToHexLowercase(rule.color);

    // List Field Formatting
    if (rule.value && isListFieldFormatting(rule.value)) {
      const listValue = rule.value as Record<string, any>;
      // Check each item in the list formatting
      for (const [itemName, config] of Object.entries(listValue)) {
        if (
          !config ||
          typeof config !== 'object' ||
          config.isDisabled ||
          !config.color
        ) {
          continue;
        }

        const color: string = normalizeColorToHexLowercase(config.color);
        maybeAddColorWarning(color, rule, itemName);
        addColorUsage(rulesByColor, color, rule);
      }
      return;
    }

    // Color Range Formatting
    if (rule.value && isColorRangeFormatting(rule.value)) {
      const rangeValue = rule.value as ColorRangeFormatting;
      // Check each point in the color range (min, mid, max)
      for (const point of COLOR_RANGE_POINTS) {
        const pointConfig = rangeValue[point];
        if (
          !pointConfig ||
          typeof pointConfig !== 'object' ||
          !pointConfig.color
        ) {
          continue;
        }

        const color: string = normalizeColorToHexLowercase(pointConfig.color);
        maybeAddColorWarning(color, rule, point);
        addColorUsage(rulesByColor, color, rule);
      }

      return;
    }

    // Global Rule Color
    maybeAddColorWarning(ruleColor, rule);
    addColorUsage(rulesByColor, ruleColor, rule);
  });

  return colorWarningsByKey;
};
